import { Loader2Icon } from 'lucide-react';

import type { IngestionState } from '../model/media-tile';

/**
 * What ingestion is doing with an asset.
 *
 * Nothing at all when the row is catalogued, which is the common case: a panel that reported
 * "finished" on every finished asset would be noise on all but the few that are not. Nothing either
 * where the caller has left the unfinished wording out, which is how a section already showing what
 * a person wrote avoids also claiming it holds nothing.
 *
 * The wording comes from the caller rather than from here. One model call fills the tags and the
 * descriptions together, so the same state describes both, but a person reading the Tags section
 * wants to be told about tags and a person reading Descriptions wants to be told about descriptions.
 */
export const IngestionNotice = ({
  state,
  pendingMessage,
  unfinishedMessage,
  testId,
}: {
  state?: IngestionState;
  pendingMessage: string;
  /** Left out where the section has something on screen that would contradict it. */
  unfinishedMessage?: string;
  /** Suffixed with the state, so two notices in one panel stay tellable apart. */
  testId: string;
}) => {
  const pending = state === 'pending';
  const message = pending ? pendingMessage : unfinishedMessage;

  if (state === undefined || message === undefined) {
    return null;
  }

  return (
    <p
      data-testid={`${testId}-${state}`}
      aria-live={pending ? 'polite' : undefined}
      className="flex items-center gap-2 text-body-md text-foreground-alt"
    >
      {pending && <Loader2Icon aria-hidden="true" className="size-3.5 shrink-0 animate-spin" />}
      {message}
    </p>
  );
};
